import { motion } from 'framer-motion';
import { CheckCircle2, Circle, Loader2 } from 'lucide-react';

import { roadmap } from '@marketing/data/roadmap';
import { fadeUp, revealOnView, stagger } from '@marketing/motion/variants';

// ============================================================
// RoadmapBar — barra M0→M8 com estado factual
// ============================================================

const ICON_BY_STATE = {
  done: CheckCircle2,
  active: Loader2,
  planned: Circle,
} as const;

const STATE_LABEL = {
  done: 'Entregue',
  active: 'Em desenvolvimento',
  planned: 'Planejado',
} as const;

export function RoadmapBar() {
  return (
    <section
      id="roadmap"
      className="lume-section"
      aria-labelledby="roadmap-title"
    >
      <div className="lume-container">
        <motion.header
          {...revealOnView}
          variants={stagger(0.08)}
          className="lume-stack lume-stack-md"
          style={{ marginBottom: 'clamp(2rem, 4vw, 3rem)', maxWidth: '52rem' }}
        >
          <motion.p variants={fadeUp} className="lume-eyebrow">Estado atual</motion.p>
          <motion.h2 variants={fadeUp} id="roadmap-title" className="lume-h2 lume-text-balance">
            Onde o programa está hoje
          </motion.h2>
          <motion.p variants={fadeUp} className="lume-body-lg lume-text-pretty">
            Sem datas comprometidas — o calendário do estudo controlado depende de aprovação
            CEP-CONEP. Apresentamos apenas o estado factual de cada marco.
          </motion.p>
        </motion.header>

        <motion.ol
          {...revealOnView}
          variants={stagger(0.06)}
          className="lume-roadmap"
          style={{ listStyle: 'none', padding: 0, margin: 0 }}
        >
          {roadmap.map((m) => {
            const Icon = ICON_BY_STATE[m.state];
            return (
              <motion.li
                key={m.id}
                variants={fadeUp}
                className="lume-roadmap__milestone"
                data-state={m.state}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginBottom: '0.4rem',
                  }}
                >
                  <Icon
                    size={18}
                    aria-hidden="true"
                    className={m.state === 'active' ? 'lume-spin' : undefined}
                    style={{
                      color:
                        m.state === 'done'
                          ? 'var(--color-severity-stable-fg)'
                          : m.state === 'active'
                          ? 'var(--color-warm-700)'
                          : 'var(--color-fg-subtle)',
                    }}
                  />
                  <span
                    style={{
                      fontFamily: 'var(--font-display)',
                      fontSize: '0.8rem',
                      fontWeight: 700,
                      color: 'var(--color-fg-subtle)',
                      letterSpacing: '0.05em',
                    }}
                  >
                    {m.label} · {STATE_LABEL[m.state]}
                  </span>
                </div>
                <h3 className="lume-h3" style={{ fontSize: '1rem', marginBottom: '0.4rem' }}>
                  {m.short}
                </h3>
                <p
                  style={{
                    margin: 0,
                    fontSize: '0.85rem',
                    color: 'var(--color-fg-muted)',
                    lineHeight: 1.5,
                  }}
                >
                  {m.body}
                </p>
              </motion.li>
            );
          })}
        </motion.ol>

        <style>{`
          @keyframes lume-spin { to { transform: rotate(360deg); } }
          .lume-spin { animation: lume-spin 1.6s linear infinite; }
          @media (prefers-reduced-motion: reduce) { .lume-spin { animation: none; } }
        `}</style>
      </div>
    </section>
  );
}
